import { useMemo } from 'react';
import SectionTitle from '../../../components/common/SectionTitle/SectionTitle';
import HorizontalBarChart, {
  type HorizontalBarItem,
} from '../../../components/common/charts/HorizontalBarChart';
import {
  buildLossReasonRows,
  buildSellerRows,
  commercialConversionRate,
  commercialGoalProgress,
  formatCommercialGoalSummary,
  formatContractedRevenue,
  formatDashboardCount,
  formatDashboardPercent,
  isFullGoalMonth,
  type CommercialGoalsDashboard,
} from '../../../utils/commercialGoalsDashboard';
import styles from './CommercialGoalsBoard.module.css';

interface CommercialGoalsBoardProps {
  snapshot: CommercialGoalsDashboard;
}

const CommercialGoalsBoard = ({ snapshot }: CommercialGoalsBoardProps) => {
  const conversion = commercialConversionRate(snapshot.salesInPeriod, snapshot.leadsReceived);
  const goal = commercialGoalProgress(snapshot.goalSalesCount, snapshot.targetSales);
  const sellers = useMemo(() => buildSellerRows(snapshot.sellers), [snapshot.sellers]);
  const lossReasons = useMemo(() => buildLossReasonRows(snapshot.lossReasons), [snapshot.lossReasons]);
  const fullMonth = isFullGoalMonth(
    snapshot.dateStart,
    snapshot.dateEnd,
    snapshot.goalStartsOn,
    snapshot.goalEndsOn,
  );
  const goalItems = useMemo(
    () => buildGoalItems(snapshot.goalSalesCount, snapshot.targetSales, goal.barPercent),
    [goal.barPercent, snapshot.goalSalesCount, snapshot.targetSales],
  );
  const sellerItems = useMemo<HorizontalBarItem[]>(
    () => sellers.map((seller) => ({
      key: seller.key,
      label: seller.name,
      value: seller.sales,
      caption: `${formatDashboardCount(seller.sales)} · ${formatContractedRevenue(seller.revenueCents)}`,
    })),
    [sellers],
  );
  const lossItems = useMemo<HorizontalBarItem[]>(
    () => lossReasons.map((reason) => ({
      key: reason.key,
      label: reason.label,
      value: reason.percent,
      caption: `${formatDashboardCount(reason.count)} · ${formatDashboardPercent(reason.percent)}`,
    })),
    [lossReasons],
  );

  return (
    <div className={styles.board}>
      <div className={styles['kpi-grid']}>
        <KpiCard label="Leads recebidos" value={formatDashboardCount(snapshot.leadsReceived)} />
        <KpiCard label="Vendas" value={formatDashboardCount(snapshot.salesInPeriod)} />
        <KpiCard label="Conversão" value={formatDashboardPercent(conversion)} />
        <KpiCard label="Receita contratada" value={formatContractedRevenue(snapshot.contractedRevenueCents)} />
      </div>

      <div className={styles.split}>
        <section className={styles.card}>
          <div className={styles['goal-head']}>
            <SectionTitle text="Meta mensal" />
            <span className={styles['goal-percent']}>{formatDashboardPercent(goal.percent)}</span>
          </div>
          <p className={styles['goal-summary']}>
            {formatCommercialGoalSummary(snapshot.goalSalesCount, snapshot.targetSales)}
          </p>
          <div className={styles.chart}>
            <HorizontalBarChart
              ariaLabel="Meta mensal"
              items={goalItems}
              percentScale={snapshot.targetSales != null && snapshot.targetSales > 0}
            />
          </div>
          {!fullMonth && (
            <p className={styles['goal-note']}>
              O gráfico usa as vendas do mês inteiro, mesmo quando o período filtrado é menor.
            </p>
          )}
        </section>

        <section className={styles.card}>
          <SectionTitle text="Vendas por vendedor" />
          {sellerItems.length === 0 ? (
            <p className={styles.empty}>Nenhuma venda no período.</p>
          ) : (
            <div className={styles.chart}>
              <HorizontalBarChart
                ariaLabel="Vendas por vendedor"
                items={sellerItems}
              />
            </div>
          )}
        </section>
      </div>

      <section className={styles.card}>
        <SectionTitle text="Motivos de perda dos leads" />
        {lossItems.length === 0 ? (
          <p className={styles.empty}>Nenhuma perda no período.</p>
        ) : (
          <div className={styles.chart}>
            <HorizontalBarChart
              ariaLabel="Motivos de perda dos leads"
              items={lossItems}
              percentScale
            />
          </div>
        )}
      </section>
    </div>
  );
};

function buildGoalItems(
  sales: number,
  target: number | null,
  barPercent: number,
): HorizontalBarItem[] {
  if (target == null || target <= 0) {
    return [{
      key: 'sales',
      label: 'Vendas',
      value: sales,
      color: 'var(--color-success)',
      caption: formatDashboardCount(sales),
    }];
  }

  return [{
    key: 'progress',
    label: 'Progresso',
    value: barPercent,
    color: 'var(--color-success)',
  }];
}

const KpiCard = ({ label, value }: { label: string; value: string }) => (
  <section className={styles.card}>
    <SectionTitle text={label} />
    <p className={styles['kpi-value']}>{value}</p>
  </section>
);

export default CommercialGoalsBoard;
